// pages/legal.tsx — Terms, Privacy, Security (spec §4.5: real, comprehensive content).
import { Seo } from '../components/Seo';
import { SITE_URL } from '../lib/seo';

const UPDATED = 'July 11, 2026';

interface Block {
  h: string;
  p?: string;
  list?: string[];
}

function webPageJsonLd(title: string, description: string, path: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${title} — UPLOAD`,
    description,
    url: `${SITE_URL}${path}`,
    dateModified: '2026-07-11',
    publisher: { '@type': 'Person', name: 'Debajyoti Saikia', url: 'https://deb0.com' },
  };
}

function LegalPage({
  title,
  description,
  path,
  sections,
}: {
  title: string;
  description: string;
  path: string;
  sections: Block[];
}) {
  return (
    <>
      <Seo
        title={`${title} — UPLOAD`}
        description={description}
        path={path}
        type="article"
        jsonLd={webPageJsonLd(title, description, path)}
      />
      <article className="mx-auto max-w-2xl py-16">
        <h1 className="text-3xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-fg-subtle">Last updated {UPDATED}</p>
        {sections.map((s) => (
          <section key={s.h} className="mt-8">
            <h2 className="text-xl font-semibold">{s.h}</h2>
            {s.p ? <p className="mt-2 opacity-80">{s.p}</p> : null}
            {s.list ? (
              <ul className="mt-2 list-disc space-y-1 pl-5 opacity-80">
                {s.list.map((li) => (
                  <li key={li}>{li}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
        <p className="mt-10 text-sm text-fg-muted">
          Questions about this page? Contact{' '}
          <a className="link" href="https://deb0.com" target="_blank" rel="noreferrer">
            Debajyoti Saikia
          </a>
          .
        </p>
      </article>
    </>
  );
}

export function Terms() {
  return (
    <LegalPage
      title="Terms"
      description="Terms of use for UPLOAD — a single-owner work digital twin. Accounts, content ownership, third-party credentials, acceptable use, AI output, and liability."
      path="/terms"
      sections={[
        {
          h: 'Acceptance of these terms',
          p: 'By creating an account or using UPLOAD ("the service"), you agree to these terms. If you do not agree, do not use the service. The service is operated by Debajyoti Saikia and is currently in active development.',
        },
        {
          h: 'What UPLOAD is',
          p: 'UPLOAD is a work digital twin. You connect material you authored — documents, notes, code and pull requests, and think-aloud voice — and the service distills how you work into a private, owner-controlled memory that you and the AI agents you authorize can recall.',
        },
        {
          h: 'One owner per account',
          p: 'Each account belongs to a single owner — your own twin. There are no organizations, teams, or sharing between owners; accounts are fully isolated from one another, and no owner can access another owner’s data or keys.',
        },
        {
          h: 'Your content is yours',
          p: 'You retain all rights to the material you upload. You grant the service only the limited permission needed to store, process, distill, and serve that material back to you and to the agents you explicitly authorize. You may export everything or delete everything at any time.',
        },
        {
          h: 'Third-party credentials & services',
          p: 'To power model inference, voice, and repository analysis you provide your own credentials (LLM keys, voice keys, Git tokens). You are responsible for complying with the terms of those providers and for only connecting credentials, repositories, and content you are authorized to use.',
        },
        {
          h: 'Acceptable use',
          list: [
            'Do not upload content you do not have the right to use.',
            'Do not attempt to access another owner’s data, keys, or agent tokens.',
            'Do not use the service to build a profile of a person other than yourself.',
            'Do not probe, overload, or attempt to bypass the gateway, authentication, or rate limits.',
          ],
        },
        {
          h: 'Agent access',
          p: 'You may mint scoped, revocable tokens that let external agents reach your memory over MCP. You are responsible for the agents you authorize and for revoking tokens you no longer trust. Data returned to an agent leaves the service boundary and is subject to that agent and its model provider.',
        },
        {
          h: 'AI-generated output',
          p: 'Distillations, recalls, and agent replies are generated by AI models and may be inaccurate or incomplete. They are not professional advice. You are responsible for reviewing any output before relying on or acting on it.',
        },
        {
          h: 'Availability & changes',
          p: 'The service is provided as-is and as-available while in development. Features may change or be removed, and availability is not guaranteed. We may update these terms; continued use after an update constitutes acceptance.',
        },
        {
          h: 'Termination',
          p: 'You may stop using the service and delete your data at any time. We may suspend or terminate access that violates these terms or that risks the security or integrity of the service.',
        },
        {
          h: 'Disclaimer & limitation of liability',
          p: 'To the maximum extent permitted by law, the service is provided without warranties of any kind, and the operator is not liable for any indirect, incidental, or consequential damages arising from your use of the service.',
        },
      ]}
    />
  );
}

export function Privacy() {
  return (
    <LegalPage
      title="Privacy"
      description="How UPLOAD handles your data: per-owner isolation, encryption at rest, third-party processors, the MCP boundary, retention, and owner export/delete/kill-switch rights."
      path="/privacy"
      sections={[
        {
          h: 'Overview',
          p: 'UPLOAD is built to hold your working self privately. You are the owner and controller of your data; the service processes it only to serve you and the agents you authorize. This page explains what is stored, how it is used, and the controls you have.',
        },
        {
          h: 'What we store',
          p: 'Two separate stores: a private Corpus (raw provenance — the material you uploaded) and a Persona (the distilled method, patterns, principles, and a knowledge index). The Persona captures HOW you work, never a copy of your content.',
        },
        {
          h: 'Information you provide',
          list: [
            'Account identity: your email, via Google sign-in.',
            'Sources: documents, notes, repositories, and voice you connect.',
            'Credentials: your own LLM, voice, and Git tokens (stored encrypted).',
            'Agent tokens: scoped tokens you mint for external agents (stored hashed).',
          ],
        },
        {
          h: 'How your data is used',
          p: 'Your sources are distilled into memory units, embedded for semantic recall, and served back to you and the agents you authorize. Credentials are used only server-side to call the providers you configured. We do not sell your data or use it to train shared models.',
        },
        {
          h: 'Third-party processors',
          list: [
            'Supabase — database, object storage, and authentication.',
            'Cloudflare — static hosting and content delivery for the site.',
            'Google — sign-in (OAuth) and your account email.',
            'Model & voice providers you configure — receive your prompts/content to generate output.',
            'Agents you authorize via MCP — receive the distilled data their scope allows.',
          ],
        },
        {
          h: 'Per-owner isolation',
          p: 'Every row, blob, and key is scoped to you. One owner’s data and keys are never visible to, or usable by, any other owner. Row-Level Security is enforced in the database, and privileged server paths additionally filter by owner in code.',
        },
        {
          h: 'Encryption at rest',
          p: 'Owner-provided keys (LLM, voice, Git tokens) are encrypted at rest with envelope encryption — a per-owner data key wrapped by a Key Vault/KMS master key — and are decrypted only inside Edge Functions. The browser never sees a raw key.',
        },
        {
          h: 'The MCP boundary',
          p: 'When you authorize an agent, the distilled data it is scoped to read leaves the service boundary and lands with that agent and its model provider. The default surface is the distilled layer (the how), not the raw Corpus. Treat authorized agents as trusted recipients of your data.',
        },
        {
          h: 'Retention',
          p: 'Your data is retained until you delete it. Deleting a source removes it and everything distilled from it; the kill switch destroys the entire Corpus and Persona. Deletions take effect immediately on request.',
        },
        {
          h: 'Your rights & controls',
          list: [
            'Export your Corpus and/or Persona at any time.',
            'Delete a single source, delete the Persona, or use the kill switch to destroy everything.',
            'Revoke any agent token instantly.',
            'Review every read and ingestion action in your audit log.',
          ],
        },
        {
          h: 'Cookies & local storage',
          p: 'The site uses local storage for your theme preference and session, and the authentication cookie required to keep you signed in. No third-party advertising or tracking cookies are used.',
        },
        {
          h: 'Children',
          p: 'The service is not directed to children and is intended for use by working professionals building a twin of themselves.',
        },
        {
          h: 'Changes to this policy',
          p: 'We may update this policy as the service evolves. Material changes will be reflected by the “last updated” date above.',
        },
      ]}
    />
  );
}

export function Security() {
  return (
    <LegalPage
      title="Security"
      description="UPLOAD security: a single authenticated gateway, Row-Level Security, envelope-encrypted secrets, scoped agent tokens, and prompt-injection defense."
      path="/security"
      sections={[
        {
          h: 'Single front door',
          p: 'All reads and writes go through the Self Gateway. The database, vector store, knowledge graph, and blob storage are private and never exposed directly to the browser or to agents.',
        },
        {
          h: 'Authentication',
          p: 'Owners authenticate with Google and act only under a verified session (JWT); the owner is always resolved from the verified token, never from the request body.',
        },
        {
          h: 'Row-Level Security',
          p: 'Row-Level Security is enabled on every table and keyed to the authenticated owner; privileged server paths additionally filter by owner in code, so the service role can never cross owners.',
        },
        {
          h: 'Server-side secrets',
          p: 'Owner keys are stored with envelope encryption (a per-owner data key wrapped by a Key Vault/KMS master key) and decrypted only inside Edge Functions. The ciphertext is bound to the owner id, so it cannot be decrypted under another owner.',
        },
        {
          h: 'Scoped agent tokens',
          p: 'Agent tokens are random secrets stored only as a SHA-256 hash. Each is scoped (read/write, and optionally to specific unit types), rate-limited per minute, individually revocable, and attributed in the audit log on every call.',
        },
        {
          h: 'Untrusted input',
          p: 'Ingested content is treated as data, never as instructions, to defend against prompt injection. Inputs are validated at the Edge boundary, and HTML is reduced to text before processing.',
        },
        {
          h: 'No autonomous external actions',
          p: 'The service only ingests and grows your memory and serves it back. It never takes external write-actions on your behalf; any action is taken by an agent you explicitly authorized.',
        },
      ]}
    />
  );
}
